import { css } from 'styled-components';

import { rem } from '../../tokens/spacing';
import type { ButtonRecipeContext, ButtonVariant } from './buttonTypes';

type RecipeFn = (ctx: ButtonRecipeContext) => ReturnType<typeof css>;

const outlineBorder = (ctx: ButtonRecipeContext, token: string): string =>
  ctx.borderColor ?? token;

const outlineColor = (ctx: ButtonRecipeContext, token: string): string =>
  ctx.fontColor ?? token;

export const BUTTON_VARIANT_RECIPES = {
  rounded: () => css`
    border-radius: var(--ui-radius-full, ${rem(33)});
    background: var(--ui-interactive);
    color: var(--ui-on-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
      background: var(--ui-interactive)
        radial-gradient(circle, transparent 1%, var(--ui-interactive) 1%) center/15000%;
    }

    &:disabled {
      background-color: var(--ui-border-muted);
      color: var(--ui-btn-disabled-color);
    }
  `,

  'rounded-secondary': ({ selected }: ButtonRecipeContext) => css`
    border-radius: var(--ui-radius-full, ${rem(33)});
    background: ${selected ? 'var(--ui-interactive)' : 'var(--ui-interactive-light)'};
    color: ${selected ? 'var(--ui-on-interactive)' : 'var(--ui-interactive)'};
  `,

  primary: () => css`
    background: var(--ui-interactive);
    color: var(--ui-on-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
      background: var(--ui-interactive)
        radial-gradient(circle, transparent 1%, var(--ui-interactive) 1%) center/15000%;
    }

    &:disabled {
      background-color: var(--ui-border-muted);
      color: var(--ui-btn-disabled-color);
    }
  `,

  'primary-icon-button': () => css`
    background: var(--ui-interactive);
    color: var(--ui-on-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
      background: var(--ui-interactive)
        radial-gradient(circle, transparent 1%, var(--ui-interactive) 1%) center/15000%;
    }

    &:disabled {
      background-color: var(--ui-border-muted);
      color: var(--ui-btn-disabled-color);
    }
  `,

  'light-primary': () => css`
    background: var(--ui-interactive-light);
    color: var(--ui-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
    }

    &:disabled {
      background-color: var(--ui-border-muted);
      color: var(--ui-btn-disabled-color);
    }
  `,

  'primary-outline': () => css`
    background: var(--ui-surface);
    color: var(--ui-interactive);
    border-color: var(--ui-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
    }

    &:disabled {
      border-color: var(--ui-border);
      color: var(--ui-text-disabled);
    }
  `,

  'primary-dark': () => css`
    background: var(--ui-surface);
    color: var(--ui-text);
    border-color: var(--ui-border);

    &:not(:disabled):hover {
      border-color: var(--ui-text-subtle);
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
    }

    &:disabled {
      background-color: var(--ui-background-muted);
      color: var(--ui-btn-disabled-color);
    }
  `,

  'primary-danger': () => css`
    background: var(--ui-destructive);
    color: var(--ui-text-inverse);
    box-shadow: ${rem(0)} ${rem(2)} ${rem(4)} var(--ui-shadow-color);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
    }

    &:disabled {
      background-color: var(--ui-background-muted);
      color: var(--ui-btn-disabled-color);
    }
  `,

  'primary-import': () => css`
    background: var(--ui-surface);
    color: var(--ui-interactive);
    border-color: var(--ui-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
    }

    &:disabled {
      border-color: var(--ui-border);
      color: var(--ui-text-disabled);
    }
  `,

  approve: () => css`
    background: var(--ui-status-success);
    color: var(--ui-text-inverse);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
    }

    &:disabled {
      background-color: var(--ui-border-muted);
      color: var(--ui-btn-disabled-color);
    }
  `,

  secondary: () => css`
    background: var(--ui-interactive-light);
    color: var(--ui-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(3)} ${rem(0)} var(--ui-shadow-color);
      background: var(--ui-interactive-light)
        radial-gradient(circle, transparent 1%, var(--ui-interactive-light) 1%) center/15000%;
    }
  `,

  'secondary-dark': () => css`
    background: var(--ui-background-muted);
    color: var(--ui-text-muted);
    border-color: var(--ui-border);

    &:not(:disabled):active {
      border-color: var(--ui-border);
    }

    &:not(:disabled):hover {
      background: var(--ui-status-success-muted);
      color: var(--ui-status-success);
      border-color: var(--ui-status-success);

      .material-symbols-outlined {
        color: var(--ui-status-success);
      }
    }
  `,

  'rounded-icon-secondary': () => css`
    border-radius: var(--ui-radius-full, ${rem(100)});
    background: var(--ui-interactive-light);
    color: var(--ui-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(3)} ${rem(0)} var(--ui-shadow-color);
      background: var(--ui-interactive-light)
        radial-gradient(circle, transparent 1%, var(--ui-interactive-light) 1%) center/15000%;
    }
  `,

  'rounded-icon-secondary-dark': () => css`
    border-radius: var(--ui-radius-full, ${rem(100)});
    background: var(--ui-background-muted);
    color: var(--ui-text-muted);
    border-color: var(--ui-border);

    &:not(:disabled):active {
      border-color: var(--ui-border);
    }

    &:not(:disabled):hover {
      border-color: var(--ui-text-muted);

      .material-symbols-outlined {
        color: var(--ui-text-muted);
      }
    }
  `,

  'small-icon-dark': () => css`
    border-radius: var(--ui-radius-md);    
    background: var(--ui-background);
    color: var(--ui-text-muted);
    border: ${rem(1)} solid var(--ui-border);
    padding: ${rem(4)};
    min-width: ${rem(25)};
    min-height: ${rem(25)};
    width: auto;

    &:not(:disabled):active {
      border-color: var(--ui-border);
    }

    &:not(:disabled):hover {
      border-color: var(--ui-text-muted);

      .material-symbols-outlined {
        color: var(--ui-text-muted);
      }
    }
  `,

  'secondary-white': () => css`
    background: var(--ui-surface);
    border-color: var(--ui-border);
    color: var(--ui-text-muted);

    &:not(:disabled):active {
      color: var(--ui-text);
      border-color: var(--ui-text-subtle);
    }

    &:not(:disabled):hover {
      border-color: var(--ui-text-subtle);
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-shadow-color);
    }
  `,

  'secondary-red': () => css`
    background: var(--ui-status-danger-muted);
    border-color: var(--ui-btn-red-outline-border);
    color: var(--ui-destructive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-red-shadow-hover);
    }

    &:not(:disabled):active {
      box-shadow: ${rem(0)} ${rem(1)} ${rem(4)} var(--ui-btn-red-shadow-active);
    }

    &:disabled {
      border-color: var(--ui-border);
      color: var(--ui-text-disabled);
    }
  `,

  'secondary-gray': () => css`
    background: var(--ui-border-muted);
    border-color: var(--ui-border-muted);
    color: var(--ui-text-muted);

    &:not(:disabled):active {
      color: var(--ui-text);
      border-color: var(--ui-text-subtle);
    }

    &:not(:disabled):hover {
      border-color: var(--ui-text-subtle);
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-shadow-color);
    }
  `,

  'secondary-gray-dark': () => css`
    background: var(--ui-border-strong);
    border-color: var(--ui-border-muted);
    color: var(--ui-text-inverse);

    &:not(:disabled):active {
      border-color: var(--ui-text-subtle);
    }

    &:not(:disabled):hover {
      border-color: var(--ui-text-subtle);
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-shadow-color);
    }

    &:focus-visible {
      color: var(--ui-text-inverse);
    }
  `,

  'secondary-outline': (ctx) => css`
    background: var(--ui-surface);
    border-color: ${outlineBorder(ctx, 'var(--ui-btn-outline-border)')};
    color: ${outlineColor(ctx, 'var(--ui-interactive)')};

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-shadow);
    }

    &:not(:disabled):active {
      box-shadow: ${rem(0)} ${rem(1)} ${rem(4)} var(--ui-btn-shadow);
    }

    &:disabled {
      border-color: var(--ui-border);
      color: var(--ui-text-disabled);
    }
  `,

  'secondary-red-outline': (ctx) => css`
    background: var(--ui-surface);
    border-color: ${outlineBorder(ctx, 'var(--ui-btn-red-outline-border)')};
    color: ${outlineColor(ctx, 'var(--ui-destructive)')};

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} var(--ui-btn-red-shadow-hover);
    }

    &:not(:disabled):active {
      box-shadow: ${rem(0)} ${rem(1)} ${rem(4)} var(--ui-btn-red-shadow-active);
    }

    &:disabled {
      border-color: var(--ui-border);
      color: var(--ui-text-disabled);
    }
  `,

  error: () => css`
    background: var(--ui-background-muted);
    color: var(--ui-text);
    border-color: var(--ui-border);

    .material-symbols-outlined {
      color: var(--ui-border-strong);
    }

    &:not(:disabled):hover {
      color: var(--ui-destructive);
      background: var(--ui-status-danger-muted);

      .material-symbols-outlined {
        color: var(--ui-destructive);
      }
    }

    &:not(:disabled):active {
      color: var(--ui-destructive);
      background: var(--ui-status-danger-muted);
      border-color: var(--ui-destructive);

      .material-symbols-outlined {
        color: var(--ui-destructive);
      }
    }

    &:disabled {
      border-color: var(--ui-border);

      .material-symbols-outlined {
        opacity: 0.25;
        color: var(--ui-text-subtle);
      }
    }
  `,

  success: () => css`
    background: var(--ui-background-muted);
    color: var(--ui-text);
    border-color: var(--ui-border);

    .material-symbols-outlined {
      color: var(--ui-border-strong);
    }

    &:not(:disabled):hover {
      color: var(--ui-status-success);
      background: var(--ui-status-success-muted);

      .material-symbols-outlined {
        color: var(--ui-status-success);
      }
    }

    &:not(:disabled):active {
      color: var(--ui-status-success);
      background: var(--ui-status-success-muted);
      border-color: var(--ui-status-success);

      .material-symbols-outlined {
        color: var(--ui-status-success);
      }
    }

    &:disabled {
      background-color: var(--ui-background-muted);
      border-color: var(--ui-border);

      .material-symbols-outlined {
        opacity: 0.25;
        color: var(--ui-text-subtle);
      }
    }
  `,

  'success-outline': () => css`
    background-color: var(--ui-surface);
    color: var(--ui-text);
    border-color: var(--ui-border);

    .material-symbols-outlined {
      color: var(--ui-border-strong);
    }

    &:not(:disabled):hover {
      color: var(--ui-status-success);
      border-color: var(--ui-status-success);

      .material-symbols-outlined {
        color: var(--ui-status-success);
      }
    }

    &:not(:disabled):active {
      color: var(--ui-status-success);
      background: var(--ui-status-success-muted);
      border-color: var(--ui-status-success);

      .material-symbols-outlined {
        color: var(--ui-status-success);
      }
    }

    &:disabled {
      background-color: var(--ui-background-muted);
      border-color: var(--ui-border);

      .material-symbols-outlined {
        opacity: 0.25;
        color: var(--ui-text-subtle);
      }
    }
  `,

  link: () => css`
    color: var(--ui-btn-accent);

    &:not(:disabled):hover {
      color: var(--ui-interactive-hover);
    }

    &:disabled {
      color: var(--ui-text-subtle);
      text-decoration: none;
    }
  `,

  ghost: () => css`
    color: var(--ui-text-muted);
    border-color: transparent;

    &:not(:disabled):hover {
      background: var(--ui-background-muted);
    }
  `,
} satisfies Record<ButtonVariant, RecipeFn>;

export function getButtonVariantRecipe(
  variant: ButtonVariant,
  ctx: ButtonRecipeContext,
): ReturnType<typeof css> {
  return BUTTON_VARIANT_RECIPES[variant](ctx);
}
